/**
 * @fileoverview fleet-util.ts — pure helpers for the Deployments landing
 * (the cross-project command center).
 * @module apps/network/consumer/src/features/deployments/fleet-util
 *
 * Adds the geo layer (region centroids + equirectangular projection + fleet
 * points for the world map) and live-URL derivation. Status, stats, and the
 * activity feed reuse `home-util` — one source of truth for "what's live".
 *
 * v1 reality: the client Project carries no region, and every app deploys to
 * `ewr` (Newark) by default until a region picker ships — so fleet points
 * currently cluster at Newark. The point/cluster shapes already carry geo, so
 * multi-region + the v2 audience-heat layer + the globe drop straight in.
 */
import type {
  ProjectLike,
  ProjectStatus,
  ProjectRollup,
} from "../../home/home-util.js";
import { eventProjectId } from "../../home/home-util.js";
import type { DeploymentStreamEvent } from "../../../../global/io/nodes/deployment/deployment-io.js";

/** A geographic anchor (a Fly region's city centroid). */
export interface GeoRegion {
  readonly code: string;
  readonly label: string;
  readonly lat: number;
  readonly lng: number;
}

/** Fly region centroids. The default deploy region is `ewr`. */
export const FLY_REGIONS: Record<string, GeoRegion> = {
  ewr: { code: "ewr", label: "Newark",       lat: 40.69,  lng: -74.17 },
  iad: { code: "iad", label: "Ashburn",      lat: 39.04,  lng: -77.49 },
  lax: { code: "lax", label: "Los Angeles",  lat: 33.94,  lng: -118.41 },
  sjc: { code: "sjc", label: "San Jose",     lat: 37.36,  lng: -121.93 },
  yyz: { code: "yyz", label: "Toronto",      lat: 43.68,  lng: -79.61 },
  gru: { code: "gru", label: "São Paulo",    lat: -23.55, lng: -46.63 },
  lhr: { code: "lhr", label: "London",       lat: 51.50,  lng: -0.46 },
  fra: { code: "fra", label: "Frankfurt",    lat: 50.11,  lng: 8.68 },
  ams: { code: "ams", label: "Amsterdam",    lat: 52.31,  lng: 4.76 },
  cdg: { code: "cdg", label: "Paris",        lat: 49.01,  lng: 2.55 },
  nrt: { code: "nrt", label: "Tokyo",        lat: 35.55,  lng: 139.78 },
  sin: { code: "sin", label: "Singapore",    lat: 1.36,   lng: 103.99 },
  syd: { code: "syd", label: "Sydney",       lat: -33.87, lng: 151.21 },
  jnb: { code: "jnb", label: "Johannesburg", lat: -26.20, lng: 28.04 },
};

export const DEFAULT_REGION = "ewr";

/** Resolve a region code → GeoRegion, falling back to the default. */
export function regionOf(code: string | undefined): GeoRegion {
  const hit = code ? FLY_REGIONS[code] : undefined;
  return hit ?? FLY_REGIONS[DEFAULT_REGION]!;
}

/** Equirectangular projection → fractional {x,y} in [0,1] (0,0 = top-left,
 *  i.e. lng −180 / lat +90). Multiply by the canvas W/H to place. */
export function project(lat: number, lng: number): { x: number; y: number } {
  return { x: (lng + 180) / 360, y: (90 - lat) / 180 };
}

/** A project's live URL — custom domain if set, else the Fly default. */
export function liveUrl(p: ProjectLike): string {
  if (p.productionDomain) return `https://${p.productionDomain}`;
  return `https://liquidefy-${p.id}.fly.dev`;
}

/** One project, placed geographically, for the map + wall. */
export interface FleetPoint {
  readonly id: string;
  readonly name: string;
  readonly status: ProjectStatus;
  readonly region: GeoRegion;
  readonly url: string;
  readonly lastActivityMs: number;
}

/** Build the geo points — one per project, placed at its region. */
export function fleetPoints(
  projects: readonly ProjectLike[],
  rollup: Map<string, ProjectRollup>,
): FleetPoint[] {
  return projects.map((p) => {
    const r = rollup.get(p.id);
    return {
      id: p.id,
      name: p.name,
      status: r?.status ?? "idle",
      // v1: region picker not shipped → everything is the default region.
      region: regionOf(undefined),
      url: liveUrl(p),
      lastActivityMs: r?.lastActivityMs ?? p.createdAtMs,
    };
  });
}

/** Worst-wins status for a group (error ▸ building ▸ live ▸ idle). */
export function worstStatus(points: readonly FleetPoint[]): ProjectStatus {
  let rank = 0;
  const order: Record<ProjectStatus, number> = { idle: 0, live: 1, building: 2, error: 3 };
  let out: ProjectStatus = "idle";
  for (const p of points) {
    if (order[p.status] >= rank) { rank = order[p.status]; out = p.status; }
  }
  return out;
}

/** Apps sharing a region, for clustering on the map. */
export interface RegionCluster {
  readonly region: GeoRegion;
  readonly points: FleetPoint[];
  readonly status: ProjectStatus;
}

export function clusterByRegion(points: readonly FleetPoint[]): RegionCluster[] {
  const m = new Map<string, FleetPoint[]>();
  for (const pt of points) {
    const arr = m.get(pt.region.code) ?? [];
    arr.push(pt);
    m.set(pt.region.code, arr);
  }
  return [...m.entries()].map(([code, pts]) => ({
    region: regionOf(code),
    points: pts,
    status: worstStatus(pts),
  }));
}

// ── Scope filters (Phase 1 aggregation) ──────────────────────────
// Pass the resolved scope id-set; when scope is "all" these are no-ops since
// the set already contains every owned id.

/** Keep only projects whose id is in the resolved scope set. */
export function scopeProjects<T extends { id: string }>(
  projects: readonly T[],
  scopeIds: readonly string[],
): T[] {
  const set = new Set(scopeIds);
  return projects.filter((p) => set.has(p.id));
}

/** Keep events targeting an in-scope project; global/untargeted events stay. */
export function scopeEvents(
  events: readonly DeploymentStreamEvent[],
  scopeIds: readonly string[],
): DeploymentStreamEvent[] {
  const set = new Set(scopeIds);
  return events.filter((e) => {
    const t = eventProjectId(e);
    return t === null || set.has(t);
  });
}
